import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import QRCode from 'react-qr-code';
import UserMenu from '../components/UserMenu';
import OwnerPaymentRecords from '../components/OwnerPaymentRecords';
import OwnerPriceSettings from '../components/OwnerPriceSettings';
import ParkingLots from '../components/ParkingLots';

const OwnerDashboard = () => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [owner, setOwner] = useState(null);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    todayRevenue: 0,
    totalSessions: 0,
    todaySessions: 0,
    averageDuration: 0,
    currentlyParked: 0
  });
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stripeOnboarding, setStripeOnboarding] = useState(false);
  const [stripeMessage, setStripeMessage] = useState(null);
  const [checkingStripeStatus, setCheckingStripeStatus] = useState(false);
  const [parkingLotsCount, setParkingLotsCount] = useState(0);

  const checkInUrl = owner ? `${window.location.origin}/checkin/${owner.ownerId}` : '';

  useEffect(() => {
    fetchOwnerData();
    fetchStats();
    fetchRecentSessions();
    fetchParkingLotsCount();
  }, [docId]);

  // Handle Stripe return URL parameters
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      setStripeMessage({ type: 'success', text: 'Stripe setup completed! Checking account status...' });
      // Check the account status after returning from Stripe
      if (owner?.stripeAccountId) {
        checkStripeStatus();
      }
      // Clear the URL parameter
      setSearchParams({});
    } else if (stripeParam === 'refresh') {
      setStripeMessage({ type: 'info', text: 'Please complete your Stripe setup to start accepting payments.' });
      setSearchParams({});
    }
  }, [searchParams, owner?.stripeAccountId]);

  const checkStripeStatus = async () => {
    if (!owner?.stripeAccountId) return;

    setCheckingStripeStatus(true);
    try {
      const checkStatus = httpsCallable(functions, 'checkStripeAccountStatus');
      const result = await checkStatus({
        stripeAccountId: owner.stripeAccountId,
        docId: docId
      });

      // Refresh owner data to get updated status
      await fetchOwnerData();

      if (result.data.onboardingComplete) {
        setStripeMessage({ type: 'success', text: 'Your Stripe account is fully set up! You can now receive payments.' });
      } else {
        setStripeMessage({ type: 'warning', text: 'Please complete the remaining steps in Stripe to start receiving payments.' });
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      setCheckingStripeStatus(false);
    }
  };

  const fetchOwnerData = async () => {
    try {
      const docRef = doc(db, 'parking-lot-owners', docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setOwner({ id: docSnap.id, ...docSnap.data() });
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error fetching owner data:', error);
      navigate('/login');
    }
  };

  const fetchStats = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const sessionsQuery = query(
        collection(db, 'parking-sessions'),
        where('ownerId', '==', owner?.ownerId || '')
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      let totalRevenue = 0;
      let todayRevenue = 0;
      let totalDuration = 0;
      let currentlyParked = 0;
      let todaySessions = 0;

      sessionsSnapshot.forEach((doc) => {
        const session = doc.data();
        
        if (session.paid && session.amount) {
          totalRevenue += session.amount;
          
          const sessionDate = new Date(session.createdAt);
          if (sessionDate >= today) {
            todayRevenue += session.amount;
            todaySessions++;
          }
        }

        if (!session.checkOutTime) {
          currentlyParked++;
        }

        if (session.duration) {
          totalDuration += session.duration;
        }
      });

      const averageDuration = sessionsSnapshot.size > 0 
        ? Math.round(totalDuration / sessionsSnapshot.size) 
        : 0;

      setStats({
        totalRevenue: totalRevenue / 100,
        todayRevenue: todayRevenue / 100,
        totalSessions: sessionsSnapshot.size,
        todaySessions,
        averageDuration,
        currentlyParked
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentSessions = async () => {
    try {
      const q = query(
        collection(db, 'parking-sessions'),
        where('ownerId', '==', owner?.ownerId || ''),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const querySnapshot = await getDocs(q);
      
      const sessions = [];
      querySnapshot.forEach((doc) => {
        sessions.push({ id: doc.id, ...doc.data() });
      });
      
      setRecentSessions(sessions);
    } catch (error) {
      console.error('Error fetching recent sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParkingLotsCount = async () => {
    try {
      const q = query(collection(db, 'parking-lots'), where('ownerId', '==', owner?.ownerId || ''));
      const querySnapshot = await getDocs(q);
      setParkingLotsCount(querySnapshot.size);
    } catch (error) {
      console.error('Error fetching parking lots count:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const startStripeOnboarding = async () => {
    setStripeOnboarding(true);
    setStripeMessage(null);
    try {
      console.log('Starting Stripe onboarding for owner:', owner.ownerId);
      const createStripeAccount = httpsCallable(functions, 'createStripeConnectAccount');
      const result = await createStripeAccount({
        ownerId: owner.ownerId,
        docId: docId
      });

      console.log('Stripe Connect response:', result.data);

      if (result.data && result.data.url) {
        if (result.data.alreadyOnboarded) {
          // If already onboarded, open the Stripe dashboard
          console.log('Opening Stripe dashboard:', result.data.url);
          window.open(result.data.url, '_blank');
          setStripeMessage({ type: 'info', text: 'Opening your Stripe dashboard...' });
        } else {
          // Redirect to Stripe onboarding
          console.log('Redirecting to Stripe onboarding:', result.data.url);
          setStripeMessage({ type: 'info', text: 'Redirecting to Stripe...' });
          // Small delay to show the message before redirect
          setTimeout(() => {
            window.location.href = result.data.url;
          }, 500);
        }
      } else {
        console.error('No URL returned from Stripe Connect function');
        setStripeMessage({
          type: 'error',
          text: 'No redirect URL received from Stripe. Please check the server logs.'
        });
      }
    } catch (error) {
      console.error('Error starting Stripe onboarding:', error);
      setStripeMessage({
        type: 'error',
        text: error.message || 'Failed to start Stripe setup. Please try again.'
      });
    } finally {
      setStripeOnboarding(false);
    }
  };

  const openStripeDashboard = async () => {
    if (!owner?.stripeAccountId) return;

    try {
      const getDashboardLink = httpsCallable(functions, 'getStripeDashboardLink');
      const result = await getDashboardLink({
        stripeAccountId: owner.stripeAccountId,
        docId: docId
      });

      if (result.data.url) {
        window.open(result.data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      setStripeMessage({
        type: 'error',
        text: 'Failed to open Stripe dashboard. Please try again.'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!owner) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Owner not found</h2>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (owner.status !== 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="mt-3 text-xl font-bold text-gray-900">Account {owner.status}</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your account is currently {owner.status}. Please wait for admin approval or contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold">Parking Lot Dashboard</h1>
            </div>
            <div className="flex items-center">
              <UserMenu user={owner} userType="owner" ownerId={owner?.ownerId} />
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-2">Welcome, {owner.name}!</h2>
          <p className="text-gray-600">{owner.email}</p>
          <p className="text-gray-600 text-sm mt-1">
            <svg className="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {owner.parkingLotAddress || 'Address not provided'}
          </p>
        </div>

        {/* Stripe Status Message */}
        {stripeMessage && (
          <div className={`rounded-lg p-4 mb-6 ${
            stripeMessage.type === 'success' ? 'bg-green-50 border border-green-200' :
            stripeMessage.type === 'error' ? 'bg-red-50 border border-red-200' :
            stripeMessage.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {stripeMessage.type === 'success' && (
                  <svg className="h-5 w-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                {stripeMessage.type === 'error' && (
                  <svg className="h-5 w-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <p className={`text-sm font-medium ${
                  stripeMessage.type === 'success' ? 'text-green-800' :
                  stripeMessage.type === 'error' ? 'text-red-800' :
                  stripeMessage.type === 'warning' ? 'text-yellow-800' :
                  'text-blue-800'
                }`}>{stripeMessage.text}</p>
              </div>
              <button
                onClick={() => setStripeMessage(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Stripe Connect Section */}
        <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
                </svg>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-white">Payment Account</h3>
                  <p className="text-indigo-100 text-sm">
                    {owner.stripeOnboardingComplete
                      ? 'Your account is ready to receive payments'
                      : owner.stripeAccountId
                        ? 'Complete your setup to start receiving payments'
                        : 'Connect your Stripe account to receive payments'}
                  </p>
                </div>
              </div>
              {/* Status Badge */}
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                owner.stripeOnboardingComplete
                  ? 'bg-green-100 text-green-800'
                  : owner.stripeAccountId
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
              }`}>
                {owner.stripeOnboardingComplete
                  ? 'Active'
                  : owner.stripeAccountId
                    ? 'Pending Setup'
                    : 'Not Connected'}
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            {/* Not Connected State */}
            {!owner.stripeAccountId && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">
                    Set up your Stripe account to start accepting payments from customers.
                    The process takes just a few minutes.
                  </p>
                  <ul className="mt-3 text-sm text-gray-500 space-y-1">
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Secure payment processing
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Direct deposits to your bank
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Track earnings in real-time
                    </li>
                  </ul>
                </div>
                <button
                  onClick={startStripeOnboarding}
                  disabled={stripeOnboarding}
                  className="ml-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {stripeOnboarding ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                      </svg>
                      Connect with Stripe
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Pending Setup State */}
            {owner.stripeAccountId && !owner.stripeOnboardingComplete && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">
                    Your Stripe account has been created but setup is not complete.
                    Please finish the verification process to start accepting payments.
                  </p>
                  <div className="mt-3 flex items-center space-x-4 text-sm">
                    <span className="flex items-center text-gray-500">
                      <span className={`h-2 w-2 rounded-full mr-2 ${owner.stripeChargesEnabled ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                      Charges: {owner.stripeChargesEnabled ? 'Enabled' : 'Pending'}
                    </span>
                    <span className="flex items-center text-gray-500">
                      <span className={`h-2 w-2 rounded-full mr-2 ${owner.stripePayoutsEnabled ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                      Payouts: {owner.stripePayoutsEnabled ? 'Enabled' : 'Pending'}
                    </span>
                  </div>
                </div>
                <div className="ml-6 flex space-x-3">
                  <button
                    onClick={checkStripeStatus}
                    disabled={checkingStripeStatus}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {checkingStripeStatus ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Checking...
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh Status
                      </>
                    )}
                  </button>
                  <button
                    onClick={startStripeOnboarding}
                    disabled={stripeOnboarding}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {stripeOnboarding ? 'Loading...' : 'Continue Setup'}
                  </button>
                </div>
              </div>
            )}

            {/* Fully Connected State */}
            {owner.stripeOnboardingComplete && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">
                    Your Stripe account is fully set up and ready to receive payments.
                    Funds will be deposited directly to your linked bank account.
                  </p>
                  <div className="mt-3 flex items-center space-x-4 text-sm">
                    <span className="flex items-center text-green-600">
                      <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Charges Enabled
                    </span>
                    <span className="flex items-center text-green-600">
                      <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Payouts Enabled
                    </span>
                  </div>
                </div>
                <button
                  onClick={openStripeDashboard}
                  className="ml-6 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View Stripe Dashboard
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('lots')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'lots'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Parking Lots
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payments'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payment Records
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pricing'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pricing Settings
              </button>
              <button
                onClick={() => setActiveTab('howto')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'howto'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                How To Use
              </button>
            </nav>
          </div>
        </div>

        {activeTab === 'overview' && (
          <div>
            {/* Setup Progress Section */}
            {(!owner.stripeOnboardingComplete || parkingLotsCount === 0) ? (
              <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">Complete Your Setup</h3>
                  <p className="text-indigo-100 text-sm">
                    {owner.stripeOnboardingComplete && parkingLotsCount > 0
                      ? "You're all set!"
                      : `${(owner.stripeOnboardingComplete ? 1 : 0) + (parkingLotsCount > 0 ? 1 : 0)} of 2 steps completed`}
                  </p>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {/* Step 1: Connect Stripe */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                          owner.stripeOnboardingComplete
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          {owner.stripeOnboardingComplete ? (
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <span className="text-sm font-medium">1</span>
                          )}
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${owner.stripeOnboardingComplete ? 'text-green-600' : 'text-gray-900'}`}>
                            Connect Stripe Account
                          </p>
                          <p className="text-xs text-gray-500">
                            {owner.stripeOnboardingComplete ? 'Ready to receive payments' : 'Required to accept payments'}
                          </p>
                        </div>
                      </div>
                      {!owner.stripeOnboardingComplete && (
                        <button
                          onClick={startStripeOnboarding}
                          disabled={stripeOnboarding}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {stripeOnboarding ? 'Connecting...' : 'Connect'}
                        </button>
                      )}
                    </div>

                    {/* Step 2: Create Parking Lot */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                          parkingLotsCount > 0
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          {parkingLotsCount > 0 ? (
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <span className="text-sm font-medium">2</span>
                          )}
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${parkingLotsCount > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                            Create a Parking Lot
                          </p>
                          <p className="text-xs text-gray-500">
                            {parkingLotsCount > 0 ? `${parkingLotsCount} parking lot${parkingLotsCount > 1 ? 's' : ''} created` : 'Add your first parking location'}
                          </p>
                        </div>
                      </div>
                      {parkingLotsCount === 0 && (
                        <button
                          onClick={() => setActiveTab('lots')}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                        >
                          Create
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setActiveTab('howto')}
                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      View full setup guide →
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg className="h-6 w-6 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-green-800">Setup Complete!</p>
                    <p className="text-xs text-green-600">Your parking lot is ready to accept customers.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                        <dd className="text-lg font-medium text-gray-900">${stats.totalRevenue.toFixed(2)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Today's Revenue</dt>
                        <dd className="text-lg font-medium text-gray-900">${stats.todayRevenue.toFixed(2)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Total Sessions</dt>
                        <dd className="text-lg font-medium text-gray-900">{stats.totalSessions}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Currently Parked</dt>
                        <dd className="text-lg font-medium text-gray-900">{stats.currentlyParked}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Today's Sessions</dt>
                        <dd className="text-lg font-medium text-gray-900">{stats.todaySessions}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Avg Duration</dt>
                        <dd className="text-lg font-medium text-gray-900">{stats.averageDuration} min</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'lots' && (
          <ParkingLots owner={owner} ownerId={owner?.ownerId} />
        )}

        {activeTab === 'payments' && (
          <OwnerPaymentRecords ownerId={owner?.ownerId} />
        )}

        {activeTab === 'pricing' && (
          <OwnerPriceSettings ownerId={owner?.ownerId} />
        )}

        {activeTab === 'howto' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Getting Started Guide</h3>
            <p className="text-gray-600 mb-8">
              Follow these steps to set up your parking lot and start accepting payments from customers.
            </p>

            <div className="space-y-6">
              {/* Step 1: Connect Stripe */}
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className={`flex items-center justify-center h-10 w-10 rounded-full ${
                    owner.stripeOnboardingComplete
                      ? 'bg-green-100 text-green-600'
                      : 'bg-indigo-100 text-indigo-600'
                  }`}>
                    {owner.stripeOnboardingComplete ? (
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <span className="text-lg font-semibold">1</span>
                    )}
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-lg font-semibold text-gray-900">Connect Your Stripe Account</h4>
                  <p className="mt-1 text-gray-600">
                    Connect your Stripe account to receive payments from customers. Once connected, all parking fees will be deposited directly to your bank account.
                  </p>
                  <div className="mt-3">
                    {owner.stripeOnboardingComplete ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Stripe Connected
                      </span>
                    ) : (
                      <p className="text-sm text-indigo-600 font-medium">
                        Use the "Payment Account" section above to connect your Stripe account.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 2: Create Parking Lot */}
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-100 text-indigo-600">
                    <span className="text-lg font-semibold">2</span>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-lg font-semibold text-gray-900">Create a Parking Lot</h4>
                  <p className="mt-1 text-gray-600">
                    Go to the <button onClick={() => setActiveTab('lots')} className="text-indigo-600 font-medium hover:text-indigo-800 underline">Parking Lots</button> tab and create your parking lot. Enter the name and address of your parking location.
                  </p>
                </div>
              </div>

              {/* Step 3: Share Your Link & QR Code */}
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-100 text-indigo-600">
                    <span className="text-lg font-semibold">3</span>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-lg font-semibold text-gray-900">Distribute Your Link & Print QR Code</h4>
                  <p className="mt-1 text-gray-600">
                    After creating your parking lot, you'll get a unique link and QR code. Share the link with customers or print the QR code and display it at your parking lot entrance. Customers can scan the QR code to check in and pay for parking.
                  </p>
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <strong>Tip:</strong> Print the QR code on a weatherproof sign and place it where customers can easily scan it when they arrive.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 4: Set Your Pricing */}
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-100 text-indigo-600">
                    <span className="text-lg font-semibold">4</span>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="text-lg font-semibold text-gray-900">Set Your Pricing</h4>
                  <p className="mt-1 text-gray-600">
                    Go to the <button onClick={() => setActiveTab('pricing')} className="text-indigo-600 font-medium hover:text-indigo-800 underline">Pricing Settings</button> tab to set your hourly parking rate. You can adjust this anytime to match your business needs.
                  </p>
                </div>
              </div>
            </div>

            {/* Ready to Go Section */}
            <div className="mt-8 p-6 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold text-green-800">You're Ready to Start Collecting!</h4>
                  <p className="text-green-700">
                    Once you've completed these steps, customers can start using your parking lot and you'll receive payments directly to your connected bank account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'qrcode_old' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Your Parking Lot QR Code</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="bg-white p-4 border-2 border-gray-200 rounded-lg inline-block">
                  <QRCode value={checkInUrl} size={250} level="H" />
                </div>
                <p className="mt-4 text-sm text-gray-600">
                  Print this QR code and display it at your parking lot entrance for customers to scan.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Payment Link</h4>
                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="text"
                    value={checkInUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50"
                  />
                  <button
                    onClick={() => copyToClipboard(checkInUrl)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                  >
                    Copy
                  </button>
                </div>
                
                <a
                  href={checkInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                >
                  Preview Payment Page
                </a>
                
                <div className="mt-4 p-4 bg-blue-50 rounded-md">
                  <h5 className="font-medium text-blue-900 mb-1">How it works:</h5>
                  <ol className="text-sm text-blue-800 space-y-1">
                    <li>1. Customers scan the QR code or visit the link</li>
                    <li>2. They enter their vehicle details</li>
                    <li>3. They pay for parking through our secure system</li>
                    <li>4. You receive the payment (minus processing fees)</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sessions_old' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Recent Parking Sessions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Check In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentSessions.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                        No parking sessions yet
                      </td>
                    </tr>
                  ) : (
                    recentSessions.map((session) => (
                      <tr key={session.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {session.licensePlate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(session.createdAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {session.duration || '-'} min
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${session.amount ? (session.amount / 100).toFixed(2) : '0.00'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            session.paid 
                              ? 'bg-green-100 text-green-800' 
                              : session.checkOutTime 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-blue-100 text-blue-800'
                          }`}>
                            {session.paid ? 'Paid' : session.checkOutTime ? 'Unpaid' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerDashboard;